(() => {
  'use strict';
  const root = document.documentElement;
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motion = () => !reduced.matches && root.dataset.motion !== 'off';
  const fullMotion = () => motion() && root.dataset.motion === 'full' && fine.matches;
  const $ = (s, p = document) => p.querySelector(s);
  const $$ = (s, p = document) => Array.from(p.querySelectorAll(s));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const live = (text) => {
    const el = $('#LiveStatus');
    if (el) el.textContent = text;
  };
  let toastTimer;
  const toast = (text) => {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.hidden = true;
    }, 4500);
  };
  const focusReturn = new WeakMap();
  function openDialog(dialog, trigger) {
    if (!dialog) return;
    $$('dialog[open]').forEach((other) => {
      if (other !== dialog) other.close();
    });
    focusReturn.set(dialog, trigger || document.activeElement);
    if (!dialog.open) dialog.showModal();
  }
  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement) {
        const el = focusReturn.get(event.target);
        if (el?.isConnected) el.focus({ preventScroll: true });
        $$('video', event.target).forEach((video) => video.pause());
        if (event.target.id === 'QuickView') quickAbort?.abort();
      }
    },
    true,
  );
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.open), opener);
    }
    const closer = target.closest('[data-close]');
    if (closer) closer.closest('dialog')?.close();
    if (target instanceof HTMLDialogElement) {
      const bounds = target.getBoundingClientRect();
      if (
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom
      )
        target.close();
    }
    const railButton = target.closest('[data-rail-prev],[data-rail-next]');
    if (railButton) {
      const forward = railButton.hasAttribute('data-rail-next');
      const rail = document.getElementById(
        forward ? railButton.dataset.railNext : railButton.dataset.railPrev,
      );
      rail?.scrollBy({ left: rail.clientWidth * (forward ? 1 : -1), behavior: motion() ? 'smooth' : 'auto' });
    }
    const appearance = target.closest('[data-appearance]');
    if (appearance && appearance !== root) {
      root.dataset.appearance = root.dataset.appearance === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem('lunevar-appearance', root.dataset.appearance);
      } catch (_) {}
    }
    const mode = target.closest('[data-mode-toggle]');
    if (mode) {
      const active = root.dataset.mode !== 'editorial';
      root.dataset.mode = active ? 'editorial' : 'grid';
      $$('[data-mode-toggle]').forEach((button) => button.setAttribute('aria-pressed', String(active)));
    }
    const zoom = target.closest('[data-zoom]');
    if (zoom) {
      const dialog = $('#MediaZoom');
      const img = $('img', dialog);
      img.src = zoom.dataset.zoom;
      img.alt = zoom.dataset.alt || '';
      openDialog(dialog, zoom);
    }
    const videoToggle = target.closest('[data-video-toggle]');
    if (videoToggle) {
      const hero = videoToggle.closest('[data-hero]');
      const video = $('video', hero);
      if (!video) return;
      if (video.paused) {
        video
          .play()
          .then(() => {
            $('[data-hero-video]', hero).classList.add('is-playing');
            videoToggle.textContent = videoToggle.dataset.pauseLabel;
            videoToggle.setAttribute('aria-pressed', 'true');
          })
          .catch(() => toast(config.strings?.error));
      } else {
        video.pause();
        videoToggle.textContent = videoToggle.dataset.playLabel;
        videoToggle.setAttribute('aria-pressed', 'false');
      }
    }
    const menu = target.closest('.mega-menu');
    $$('.mega-menu[open]').forEach((item) => {
      if (item !== menu) item.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const modal = $('dialog[open]');
      if (modal) {event.preventDefault();modal.close();}
    }
    if (event.key === 'Escape')
      $$('.mega-menu[open]').forEach((item) => {
        item.open = false;
        $('summary', item)?.focus();
      });
  });
  let scrollPending = false;
  function updateScroll() {
    scrollPending = false;
    $$('[data-header]').forEach((el) => el.classList.toggle('is-scrolled', window.scrollY > 25));
    const max = document.documentElement.scrollHeight - innerHeight;
    root.style.setProperty('--scroll', max > 0 ? Math.min(1, scrollY / max) : 0);
  }
  addEventListener(
    'scroll',
    () => {
      if (!scrollPending) {
        scrollPending = true;
        requestAnimationFrame(updateScroll);
      }
    },
    { passive: true },
  );
  updateScroll();
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                revealObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px 50px 0px', threshold: 0.06 },
        )
      : null;
  function initMotion(scope = document) {
    $$('[data-reveal]', scope).forEach((el) => {
      if (el.dataset.revealReady) return;
      el.dataset.revealReady = 'true';
      if (revealObserver && motion()) revealObserver.observe(el);
      else el.classList.add('is-visible');
    });
    $$('[data-hero]', scope).forEach((hero) => {
      if (hero.dataset.motionReady) return;
      hero.dataset.motionReady = 'true';
      let frame = 0;
      hero.addEventListener('pointermove', (event) => {
        if (!fullMotion() || frame) return;
        frame = requestAnimationFrame(() => {
          const rect = hero.getBoundingClientRect();
          hero.style.setProperty('--hx', ((event.clientX - rect.left) / rect.width - 0.5) * 12 + 'px');
          hero.style.setProperty('--hy', ((event.clientY - rect.top) / rect.height - 0.5) * 8 + 'px');
          frame = 0;
        });
      });
      hero.addEventListener('pointerleave', () => {
        hero.style.setProperty('--hx', '0px');
        hero.style.setProperty('--hy', '0px');
      });
    });
    $$('[data-tilt],[data-magnetic]', scope).forEach((el) => {
      if (el.dataset.motionReady) return;
      el.dataset.motionReady = 'true';
      let frame = 0;
      el.addEventListener('pointermove', (event) => {
        if (!fullMotion() || frame) return;
        frame = requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width - 0.5;
          const y = (event.clientY - r.top) / r.height - 0.5;
          el.style.transform = el.hasAttribute('data-tilt')
            ? `perspective(1000px) rotateX(${-y * 2}deg) rotateY(${x * 2}deg)`
            : `translate(${x * 6}px,${y * 6}px)`;
          frame = 0;
        });
      });
      el.addEventListener('pointerleave', () => {
        el.style.transform = '';
      });
    });
  }
  if (revealObserver && motion()) root.classList.add('motion-ready');
  initMotion();
  document.addEventListener('shopify:section:load', (event) => {
    initMotion(event.target);
    updateScroll();
  });
  document.addEventListener('shopify:block:select', (event) => {
    event.target.scrollIntoView({ block: 'center', behavior: 'auto' });
    event.target.classList.add('is-visible');
  });
  reduced.addEventListener('change', () => {
    if (!motion()) {
      root.classList.remove('motion-ready');
      $$('video').forEach((v) => v.pause());
    }
  });
  if (root.dataset.cursor === 'true' && fullMotion()) {
    const halo = $('.cursor-halo');
    let frame = 0;
    addEventListener(
      'pointermove',
      (event) => {
        if (!fullMotion() || frame) return;
        frame = requestAnimationFrame(() => {
          const interactive = Boolean(event.target.closest('a,button,summary,input'));
          halo.classList.toggle('is-hover', interactive);
          const half = interactive ? 24 : 16;
          halo.style.transform = `translate(${event.clientX - half}px,${event.clientY - half}px)`;
          frame = 0;
        });
      },
      { passive: true },
    );
  }
  let cartBusy = false;
  const drawer = () => $('#CartDrawer');
  function cartError(message) {
    const el = $('[data-cart-error]', drawer());
    if (el) el.textContent = message;
    const recovery = $('.cart-recovery', drawer());
    if (recovery) recovery.hidden = false;
    live(message);
  }
  function renderCart(html) {
    const fresh = $('[data-cart-content]', parse(html));
    const current = $('[data-cart-content]', drawer());
    if (!fresh || !current) throw new Error(config.strings.refresh_error);
    current.replaceWith(fresh);
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = fresh.dataset.itemCount;
    });
  }
  async function refreshCart() {
    const url = new URL(config.cart, location.href);
    url.searchParams.set('section_id', 'cart-drawer');
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(config.strings.refresh_error);
    renderCart(await response.text());
  }
  async function showCart(trigger) {
    openDialog(drawer(), trigger);
    if (cartBusy) return;
    cartBusy = true;
    drawer().setAttribute('aria-busy', 'true');
    try {
      await refreshCart();
    } catch (error) {
      cartError(error.message);
    } finally {
      cartBusy = false;
      drawer().removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-open]');
    if (link && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      showCart(link);
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeCart(remove.dataset.key, 0, remove);
    }
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (input && input.checkValidity()) changeCart(input.dataset.key, Number(input.value), input);
    else if (input) {
      input.reportValidity();
      input.value = input.defaultValue;
    }
  });
  async function changeCart(key, quantity, trigger) {
    if (cartBusy) return;
    cartBusy = true;
    drawer().setAttribute('aria-busy', 'true');
    const previousId = trigger.id;
    $$('[data-cart-quantity]', drawer()).forEach((el) => {
      el.disabled = true;
    });
    const checkout = $('[name=checkout]', drawer());
    if (checkout) checkout.disabled = true;
    let confirmed = false;
    try {
      const response = await fetch(config.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: ['cart-drawer'],
          sections_url: location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status)
        throw new Error(data.description || data.message || config.strings.error);
      confirmed = true;
      if (data.sections?.['cart-drawer']) renderCart(data.sections['cart-drawer']);
      else await refreshCart();
      const newInput = previousId ? document.getElementById(previousId) : $('[data-close]', drawer());
      newInput?.focus({ preventScroll: true });
      live(config.strings.updated);
      document.dispatchEvent(
        new CustomEvent('lunevar:cart-updated', { detail: { itemCount: data.item_count } }),
      );
    } catch (error) {
      const message = confirmed
        ? config.strings.refresh_error
        : error instanceof TypeError
          ? config.strings.request_error
          : error.message;
      try {
        await refreshCart();
      } catch (_) {}
      cartError(message);
    } finally {
      cartBusy = false;
      drawer().removeAttribute('aria-busy');
      $$('[data-cart-quantity]', drawer()).forEach((el) => {
        el.disabled = false;
      });
      const btn = $('[name=checkout]', drawer());
      if (btn) btn.disabled = false;
    }
  }
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    if (
      cartBusy ||
      form.dataset.busy ||
      !form.checkValidity() ||
      form.closest('product-view[aria-busy=true]')
    )
      return;
    if (!$('[name=id]', form)?.value) return;
    cartBusy = true;
    form.dataset.busy = 'true';
    const submit = event.submitter || $('[type=submit]', form);
    const buttons = $$('button[type=submit]', form);
    if (submit && !buttons.includes(submit)) buttons.push(submit);
    buttons.forEach((el) => {
      el.disabled = true;
      el.classList.add('is-loading');
    });
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.textContent = '';
    let confirmed = false;
    try {
      const body = new FormData(form);
      body.set('sections', 'cart-drawer');
      body.set('sections_url', location.pathname);
      const response = await fetch(config.add, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status)
        throw new Error(data.description || data.message || config.strings.error);
      confirmed = true;
      if (data.sections?.['cart-drawer']) renderCart(data.sections['cart-drawer']);
      else await refreshCart();
      openDialog(drawer(), submit);
      live(config.strings.added);
      toast(config.strings.added);
      document.dispatchEvent(new CustomEvent('lunevar:cart-updated'));
    } catch (error) {
      const message = confirmed
        ? config.strings.refresh_error
        : error instanceof TypeError
          ? config.strings.request_error
          : error.message;
      if (errorEl) errorEl.textContent = message;
      if (confirmed) {
        openDialog(drawer(), submit);
        cartError(message);
      } else live(message);
    } finally {
      cartBusy = false;
      delete form.dataset.busy;
      buttons.forEach((el) => {
        el.disabled = false;
        el.classList.remove('is-loading');
      });
    }
  });
  let quickAbort;
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-quick-url]');
    if (!trigger || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    quickAbort?.abort();
    quickAbort = new AbortController();
    const dialog = $('#QuickView');
    const content = $('[data-quick-content]', dialog);
    content.replaceChildren();
    const skeleton = document.createElement('div');
    skeleton.className = 'loading-shell';
    skeleton.setAttribute('role', 'status');
    skeleton.setAttribute('aria-label', config.strings.loading);
    content.append(skeleton);
    openDialog(dialog, trigger);
    try {
      const url = new URL(trigger.dataset.quickUrl, location.href);
      url.searchParams.set('section_id', 'quick-product');
      const response = await fetch(url, { signal: quickAbort.signal });
      if (!response.ok) throw new Error(config.strings.error);
      const product = $('product-view', parse(await response.text()));
      if (!product) throw new Error(config.strings.error);
      content.replaceChildren(product);
      initMotion(content);
    } catch (error) {
      if (error.name === 'AbortError') return;
      content.replaceChildren();
      const message = document.createElement('p');
      message.textContent = config.strings.error;
      const link = document.createElement('a');
      link.href = trigger.href;
      link.className = 'button';
      link.textContent = trigger.getAttribute('aria-label') || trigger.textContent.trim();
      content.append(message, link);
    }
  });
  class ProductView extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.request = null;
      this.addEventListener(
        'change',
        (event) => {
          if (event.target.matches('[data-option-value-id]')) this.updateVariant(event.target);
        },
        { signal: this.abort.signal },
      );
      this.addEventListener(
        'click',
        (event) => {
          const target = event.target.closest('[data-media-target]');
          if (target) {
            const slide = $(`[data-media-id="${CSS.escape(target.dataset.mediaTarget)}"]`, this);
            const track = $('.gallery-track', this);
            if (slide && track)
              track.scrollTo({ left: slide.offsetLeft, behavior: motion() ? 'smooth' : 'auto' });
          }
        },
        { signal: this.abort.signal },
      );
      const track = $('.gallery-track', this);
      const selected = $('[data-selected-media]', this);
      requestAnimationFrame(() => {
        if (track && selected) track.scrollLeft = selected.offsetLeft;
      });
      if (track && 'IntersectionObserver' in window) {
        this.mediaObserver = new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              $$('[data-media-target]', this).forEach((button) => {
                if (button.dataset.mediaTarget === entry.target.dataset.mediaId)
                  button.setAttribute('aria-current', 'true');
                else button.removeAttribute('aria-current');
              });
              const counter = $('[data-gallery-current]', this);
              if (counter) counter.textContent = String($$('.gallery-slide', this).indexOf(entry.target) + 1).padStart(2, '0');
              $$('.gallery-slide', this).forEach((slide) => {
                if (slide !== entry.target) $$('video', slide).forEach((video) => video.pause());
              });
            }),
          { root: track, threshold: 0.65 },
        );
        $$('.gallery-slide', this).forEach((slide) => this.mediaObserver.observe(slide));
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.request?.abort();
      this.mediaObserver?.disconnect();
    }
    async updateVariant(input) {
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      const values = $$('[data-option-value-id]:checked', this).map((el) => el.dataset.optionValueId);
      const url = new URL(input.dataset.productUrl || this.dataset.productUrl, location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('option_values', values.join(','));
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error(config.strings.option_error);
        const html = await response.text();
        if (request !== this.request || !this.isConnected) return;
        const fresh = $('product-view', parse(html));
        if (!fresh) throw new Error(config.strings.option_error);
        if (this.dataset.domPrefix) window.LunevarPrefixProduct?.(fresh, this.dataset.domPrefix);
        const focusId = input.id;
        const selectedVariant = fresh.dataset.variantId;
        const isQuick = this.dataset.quick === 'true';
        const nextUrl = new URL(input.dataset.productUrl || this.dataset.productUrl, location.href);
        if (selectedVariant) nextUrl.searchParams.set('variant', selectedVariant);
        else nextUrl.searchParams.set('option_values', values.join(','));
        this.replaceWith(fresh);
        document.getElementById(focusId)?.focus({ preventScroll: true });
        if (!isQuick && !window.LunevarOffline) {
          history.replaceState({}, '', nextUrl.pathname + nextUrl.search);
          $$('product-recommendations').forEach((el) => el.removeAttribute('data-loaded'));
        }
        live($('.stock-status', fresh)?.textContent.trim() || '');
      } catch (error) {
        if (request === this.request && error.name !== 'AbortError') {
          const message = $('[data-form-error]', this);
          if (message) message.textContent = config.strings.option_error;
          $$('[name=id]', this).forEach((el) => {
            el.disabled = true;
          });
          $$('button[type=submit]', this).forEach((el) => {
            el.disabled = true;
          });
          live(config.strings.option_error);
        }
      } finally {
        if (request === this.request && this.isConnected) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-view', ProductView);
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!$('.product-card', this)) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.observer.disconnect();
              this.load();
            }
          },
          { rootMargin: '200px' },
        );
        this.observer.observe(this);
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.request?.abort();
    }
    async load() {
      this.request = new AbortController();
      try {
        const response = await fetch(this.dataset.url, { signal: this.request.signal });
        if (!response.ok) return;
        const fresh = $('product-recommendations', parse(await response.text()));
        if (fresh) {
          this.innerHTML = fresh.innerHTML;
          this.dataset.loaded = 'true';
          initMotion(this);
        }
      } catch (_) {}
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type=search]', this);
      this.results = $('[data-predictive-results]', this);
      this.abort = new AbortController();
      this.input.addEventListener(
        'input',
        () => {
          clearTimeout(this.timer);
          this.request?.abort();
          this.timer = setTimeout(() => this.search(), 220);
        },
        { signal: this.abort.signal },
      );
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.request?.abort();
    }
    async search() {
      const value = this.input.value.trim();
      if (value.length < 2) {
        this.results.replaceChildren();
        return;
      }
      this.request = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(config.predictive, location.href);
        url.searchParams.set('q', value);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('section_id', 'predictive-search');
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error();
        const content = $('.predictive-list', parse(await response.text()));
        if (this.input.value.trim() === value) this.results.replaceChildren(...(content ? [content] : []));
      } catch (error) {
        if (error.name !== 'AbortError') this.results.replaceChildren();
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);
})();
